import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "step",
    "nextButton",
    "feedbackCheckbox",
    "wizardStepField",
    "submitButton",
    "motivationRadio",
    "stepTwoSubtitle",
    "rgpdCheckbox"
  ]

  static values = {
    companyAppointment: Boolean,
    initialStep: { type: Number, default: 1 },
    completed: { type: Boolean, default: false },
    stepTwoSubtitleDefault: String,
    stepTwoSubtitleWithSelection: String
  }

  connect () {
    if (this.completedValue) return

    this.restoreFeedbackFromLocalStorage()

    const storedStep = parseInt(localStorage.getItem("reviewWizardStep"), 10)
    const step = Number.isNaN(storedStep) ? this.initialStepValue : storedStep
    this.showStep(step)
    this.toggleNextButton()
    this.toggleSubmitButton()
  }

  nextStep (event) {
    event.preventDefault()

    const nextStep = this.currentStep + 1
    this.showStep(nextStep)
    localStorage.setItem("reviewWizardStep", nextStep)
    this.saveFeedbackToLocalStorage()
  }

  previousStep (event) {
    event.preventDefault()

    this.showStep(this.currentStep - 1)
    localStorage.setItem("reviewWizardStep", this.currentStep)
  }

  toggleNextButton () {
    if (!this.hasNextButtonTarget) return

    const hasSelection = this.feedbackCheckboxTargets.some((checkbox) => checkbox.checked)
    this.nextButtonTarget.disabled = !hasSelection
  }

  toggleSubmitButton () {
    if (!this.hasSubmitButtonTarget) return

    let enabled = true

    if (this.hasRgpdCheckboxTarget) {
      enabled = this.rgpdCheckboxTarget.checked
    }

    if (this.hasMotivationRadioTarget) {
      enabled = enabled && this.motivationRadioTargets.some((radio) => radio.checked)
    }

    this.submitButtonTarget.disabled = !enabled
  }

  handleSubmitEnd (event) {
    localStorage.removeItem("reviewWizardStep")
    localStorage.removeItem("reviewWizardFeedbackItemIds")

    if (event.detail.completed) return

    const wizardStep = parseInt(this.wizardStepFieldTarget.value, 10)
    if (!Number.isNaN(wizardStep)) {
      this.showStep(wizardStep)
    }
  }

  showStep (step) {
    const availableSteps = this.stepTargets.map((element) => parseInt(element.dataset.step, 10))
    const resolvedStep = availableSteps.includes(step) ? step : availableSteps[0]

    this.currentStep = resolvedStep

    this.stepTargets.forEach((element) => {
      element.classList.toggle("d-none", parseInt(element.dataset.step, 10) !== resolvedStep)
    })

    if (this.hasWizardStepFieldTarget) {
      this.wizardStepFieldTarget.value = resolvedStep
    }

    if (resolvedStep === 2) {
      this.updateStepTwoSubtitle()
      this.toggleSubmitButton()
    }
  }

  updateStepTwoSubtitle () {
    if (!this.hasStepTwoSubtitleTarget) return

    const selectedNames = this.feedbackCheckboxTargets
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.dataset.feedbackItemName)
      .filter(Boolean)

    if (selectedNames.length === 0) {
      this.stepTwoSubtitleTarget.textContent = this.stepTwoSubtitleDefaultValue
      return
    }

    const items = this.formatFrenchList(selectedNames.map((name) => this.lowercaseFirst(name)))
    this.stepTwoSubtitleTarget.textContent = this.stepTwoSubtitleWithSelectionValue.replace("%{items}", items)
  }

  formatFrenchList (items) {
    if (items.length === 1) return items[0]
    if (items.length === 2) return `${items[0]} et ${items[1]}`

    return `${items.slice(0, -1).join(", ")} et ${items[items.length - 1]}`
  }

  lowercaseFirst (value) {
    return value.charAt(0).toLowerCase() + value.slice(1)
  }

  saveFeedbackToLocalStorage () {
    const ids = this.feedbackCheckboxTargets
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.value)

    localStorage.setItem("reviewWizardFeedbackItemIds", JSON.stringify(ids))
  }

  restoreFeedbackFromLocalStorage () {
    const stored = localStorage.getItem("reviewWizardFeedbackItemIds")
    if (!stored) return

    try {
      const ids = JSON.parse(stored)
      this.feedbackCheckboxTargets.forEach((checkbox) => {
        checkbox.checked = ids.includes(checkbox.value)
      })
    } catch (_error) {
      localStorage.removeItem("reviewWizardFeedbackItemIds")
    }
  }
}
